﻿import { RecoverableBinding, recoverableField } from "../../../ServiceDesk/recovery/recoverable-control";
import { WindowMWF } from "../../namespace-globals";
import { LoggingService } from "../../../ServiceDesk/sdServices/loggingSvc";
import { LocalizationService } from "../../localization/localization";

export class DatePickerBinding extends RecoverableBinding {
  /** Label for the date picker, one-time binding. */
  public label = "@?";

  /** Default date to be displayed. Expects javascript Date object, one-time binding.*/
  public defaultValue = "<";

  /** fn that is called on changed date. Parameter 'dateSelected' indicates the date which the user selected.*/
  public onDateChange = "&";

  /** fn that is called on when the picker menu is close due to cancel or date selection.*/
  public onClose = "&";

  /** ServiceDesk.Controls.DatePicker.DatePickerMode. Sets whether it is a date picker, time picker, or 24 hour time picker, one-time binding.*/
  public datePickerMode = "@?";

  /** Callback raised when the control has been initalized, which returns an instance of a delegate controller for this date picker, see DatePickerDelegateObject for more details */
  public initialized = "&?";

  /** The identifier for all business intelligence (BI) actions triggered by this component, one-time binding.*/
  public biId = "@?";

  /** Input binding for value, takes in date and updates control. */
  public value = "<?";
}

("use strict");

const datePickerClass = "data-date-time-picker";
export const monthClass = "month";
export const dayClass = "day";
export const yearClass = "year";
export const hourClass = "hour";
export const hour24Class = "hour24";
export const minuteClass = "minute";
export const minute24Class = "minute24";
export const amPMClass = "ampm";

export interface DateComponent {
  name: string;
  value: string;
}

export type DatePickerMode = "date" | "time" | "time24";

/** Exposed functionality for date picker consumers */
export interface DatePickerDelegateObject {
  /** Opens the picker selection menu, putting focus on the first visible field month in date mode or hour in time mode */
  open(): void;

  /** Closes the picker selection menu */
  close(): void;
}

interface MWFDatePickerControl {
  openPicker: (trigger: JQuery, removeFocus: boolean) => void;
  updatePicker: () => void;
  closePicker: () => void;
}

export class DatePicker {
  private controlInstance: MWFDatePickerControl;
  private defaultValue: Date;
  private monthsArray: Array<string> = [];

  public origUpdatePicker: any;
  public origClosePicker: any;
  public datePickerMode: DatePickerMode;
  public dateComponents: DateComponent[] = [];
  public dateSelectorName: string;
  public onClose: () => void;
  public onDateChange: any;
  public months: string;
  public controlElement: any;
  public biId: string;
  @recoverableField
  public returnDate: Date = undefined;
  public initialized: (val: { $sd: DatePickerDelegateObject }) => void;

  // @ngInject
  constructor(
    private $scope: any,
    private $window: WindowMWF,
    private loggingService: LoggingService,
    private $timeout: ng.ITimeoutService,
    sdLocalizationService: LocalizationService
  ) {
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_JANUARY_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_FEBRUARY_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_MARCH_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_APRIL_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_MAY_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_JUNE_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_JULY_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_AUGUST_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_SEPTEMBER_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_OCTOBER_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_NOVEMBER_"));
    this.monthsArray.push(sdLocalizationService.getLocalizedString("ShellCommon", "_CONTROLS_DATEPICKER_DECEMBER_"));
    this.months = this.monthsArray.join(",");
  }

  private monthInNumber(month: string): number {
    let result = this.monthsArray.indexOf(month);
    if (result == -1) {
      this.loggingService.logError("Invalid month :".concat(month));
    }
    return result;
  }

  private monthInText(month: number): string {
    return this.monthsArray[month];
  }

  public $onInit() {
    let setCurrentDate = false;
    if (this.defaultValue) {
      try {
        this.setDate(this.defaultValue);
      } catch (e) {
        this.loggingService.logError({
          name: "DatePicker.Initialize",
          message: "Failed to set the Init Date",
          stack: e
        });
        setCurrentDate = true;
      }
    } else {
      setCurrentDate = true;
    }
    if (setCurrentDate) {
      this.setDate(new Date());
    }

    if (angular.isFunction(this.initialized)) {
      let delegate: DatePickerDelegateObject = {
        open: () => {
          this.controlInstance.openPicker(this.controlElement.children[0], false);
        },
        close: () => {
          this.controlInstance.closePicker();
        }
      };

      this.initialized({ $sd: delegate });
    }
  }

  public $onChanges(changesObject: ng.IOnChangesObject) {
    if (changesObject.value) {
      let trackedChange = changesObject.value;
      if (trackedChange.currentValue instanceof Date) {
        try {
          this.setDate((<any>trackedChange).currentValue);
        } catch (e) {
          this.loggingService.logError({
            name: "ServiceDesk.Controls.DatePicker.ValueChanged",
            message: "Failed to set an updated date",
            stack: e
          });
        }
      }
    }
  }

  public $sdRecoveryOnComplete(data: any) {
    if (data.returnDate) {
      this.setDate(data.returnDate);
      if (this.onDateChange) {
        this.onDateChange({ dateSelected: this.returnDate });
      }
    }
  }

  private setDate(date: Date) {
    this.dateComponents = [];
    switch (this.datePickerMode) {
      case "time24":
        this.dateComponents.push({ name: hour24Class, value: date.getHours().toString() });
        this.dateComponents.push({ name: minute24Class, value: date.getMinutes().toString() });
        this.dateSelectorName = "time-selector24";
        break;
      case "time":
        let hours = date.getHours();
        if (hours > 12) {
          hours -= 12;
        }
        if (hours == 0) {
          hours = 12;
        }
        this.dateComponents.push({ name: hourClass, value: hours.toString() });
        this.dateComponents.push({ name: minuteClass, value: date.getMinutes().toString() });
        this.dateComponents.push({ name: amPMClass, value: date.getHours() > 11 ? `PM` : `AM` });
        this.dateSelectorName = "time-selector12";
        break;
      case "date":
      default:
        this.dateComponents.push({ name: monthClass, value: this.monthInText(date.getMonth()) });
        this.dateComponents.push({ name: dayClass, value: date.getDate().toString() });
        this.dateComponents.push({ name: yearClass, value: date.getFullYear().toString() });
        this.dateSelectorName = "date-selector";
        this.datePickerMode = "date";
    }
  }

  public initialize($scope: ng.IScope, element: JQuery, attrs: ng.IAttributes): void {
    this.controlElement = element.find(".c-date-time-picker")[0];
    // bindings resolution needs a delay if this control is wrapped inside another component
    this.$timeout(() => {
      this.controlInstance = new this.$window.mwf["DateTimePicker"](this.controlElement);
      this.origUpdatePicker = this.controlInstance.updatePicker.bind(this.controlInstance);
      this.origClosePicker = this.controlInstance.closePicker.bind(this.controlInstance);
      this.controlInstance.updatePicker = this.updatePicker.bind(this);
      this.controlInstance.closePicker = this.closePicker.bind(this);
    }, 0);
  }

  private getSetValueForClass(dateFieldClass: string) {
    return this.controlElement.querySelector(`[${datePickerClass}='${dateFieldClass}']`).textContent;
  }

  public closePicker(): void {
    this.origClosePicker();
    this.onClose();
  }

  public updatePicker(): void {
    this.origUpdatePicker();
    switch (this.datePickerMode) {
      case "date":
        let month = this.monthInNumber(this.getSetValueForClass(monthClass));
        let day = this.getSetValueForClass(dayClass);
        let year = this.getSetValueForClass(yearClass);
        this.returnDate = new Date(year, month, day);
        break;
      case "time":
        let hour = parseInt(this.getSetValueForClass(hourClass));
        let ampm = this.getSetValueForClass(amPMClass);
        let minute = this.getSetValueForClass(minuteClass);
        if (ampm == "PM" && hour != 12) {
          hour = hour + 12;
        } else if (ampm == "AM" && hour == 12) {
          hour = 0;
        }
        this.returnDate = new Date(0, 0, 0, hour, minute);
        break;
      case "time24":
        hour = this.getSetValueForClass(hour24Class);
        minute = this.getSetValueForClass(minute24Class);
        this.returnDate = new Date(0, 0, 0, hour, minute);
        break;
      default:
        let errorMsg = `No date handler for ${this.datePickerMode}`;
        this.loggingService.logError(errorMsg);
        throw errorMsg;
    }

    this.onDateChange({ dateSelected: this.returnDate });
    this.$scope.$emit("updateDateTimeTelemetry");
  }
}

export function sdDatePicker(): ng.IDirective {
  return {
    scope: {},
    restrict: "E",
    templateUrl: "../ShellCommon/Controls/datePicker/datePicker.html",
    link: function ($scope: any, element: JQuery, attrs: ng.IAttributes, $ctrl: DatePicker) {
      $ctrl.initialize($scope, element, attrs);
    },
    controller: DatePicker,
    controllerAs: "$ctrl",
    bindToController: <any>new DatePickerBinding()
  };
}
